// Redaction v2 (visiodocs-app#492): while the app's redaction tool is on, the viewer shows each page as the redacted copy
// will be painted by the redact-pdf service: form fields drawn into the page (AnnotationMode.ENABLE_STORAGE, set where the
// PDFViewer is made) and the PDF's own marks the copy leaves out hidden, from the list the service uses.
// Spec: docs/superpowers/specs/2026-10-18-redaction-v2-design.md, "Marking by hand" and "What is drawn into the picture".
import { PdfLosses } from './shared/model/docavea-viewer-interface';
import { isRemovedFromRedactedCopy } from './shared/model/redaction-v2-marks';

/** The fields of a pdf.js annotation (page.getAnnotations()) read here. */
export interface PdfMarkLike {
    id: string;
    subtype: string;
    pushButton?: boolean;
    hasAppearance?: boolean;
    fieldName?: string;
}

interface DocumentLike {
    numPages: number;
    getPage(pageNumber: number): Promise<{ getAnnotations(params: { intent: string }): Promise<PdfMarkLike[]> }>;
    getOutline(): Promise<{ items?: unknown[] }[] | null>;
    getAttachments(): Promise<{ [name: string]: unknown } | null>;
    annotationStorage: { setValue(id: string, value: object): void };
}

export interface MarkScan {
    removedIds: string[];   // the marks the redacted copy leaves out
    losses: PdfLosses;
}

const MEDIA = ['Sound', 'Movie', 'Screen', 'RichMedia'];
const PAGES_AT_A_TIME = 16;

const countOutline = (items: { items?: unknown[] }[] | null | undefined): number =>
    (items ?? []).reduce((sum, item) => sum + 1 + countOutline(item.items as { items?: unknown[] }[]), 0);

/**
 * Read every page's marks once: which the redacted copy leaves out, and what the copy loses.
 * @param {DocumentLike} pdfDocument the loaded pdf.js document
 * @return {Promise<MarkScan>} the removed marks' ids and the counts
 */
export const scanPdfMarks = async (pdfDocument: DocumentLike): Promise<MarkScan> => {
    const removedIds: string[] = [];
    const fieldNames = new Set<string>();
    const losses: PdfLosses = { links: 0, notes: 0, formFields: 0, bookmarks: 0, attachments: 0, freeTextBoxes: 0, mediaPlayers: 0, failed: false };
    for (let first = 1; first <= pdfDocument.numPages; first += PAGES_AT_A_TIME) {
        const pageNumbers = Array.from({ length: Math.min(PAGES_AT_A_TIME, pdfDocument.numPages - first + 1) }, (_, i) => first + i);
        const pages = await Promise.all(pageNumbers.map(async (n) => (await pdfDocument.getPage(n)).getAnnotations({ intent: 'display' })));
        for (const mark of pages.flat()) {
            if (isRemovedFromRedactedCopy(mark)) removedIds.push(mark.id);
            if (mark.subtype === 'Link') losses.links++;
            else if (mark.subtype === 'Text') losses.notes++;
            else if (mark.subtype === 'FileAttachment') losses.attachments++;
            else if (mark.subtype === 'FreeText' && !mark.hasAppearance) losses.freeTextBoxes++;
            else if (MEDIA.includes(mark.subtype)) losses.mediaPlayers++;
            else if (mark.subtype === 'Widget') fieldNames.add(mark.fieldName || mark.id);
        }
    }
    losses.formFields = fieldNames.size;
    losses.bookmarks = countOutline(await pdfDocument.getOutline());
    losses.attachments += Object.keys((await pdfDocument.getAttachments()) ?? {}).length;
    return { removedIds, losses };
};

// One scan per loaded document, shared by the redaction-mode load and COUNT_PDF_LOSSES
const scans = new WeakMap<object, Promise<MarkScan>>();
export const cachedScan = (pdfDocument: DocumentLike): Promise<MarkScan> => {
    let scan = scans.get(pdfDocument);
    if (!scan) {
        scan = scanPdfMarks(pdfDocument);
        scans.set(pdfDocument, scan);
        scan.catch(() => scans.delete(pdfDocument));
    }
    return scan;
};

/**
 * Hide the removed marks before the first page is drawn. In the canvas through the document's annotationStorage, as
 * the service does; only ever noView true, since a stored false would draw a mark the PDF flags Hidden.
 * In the HTML annotation layer through a style sheet: a sticky note without a stored appearance is an HTML icon, not
 * canvas. Popups go too: their text is never on the page.
 * @param {DocumentLike} pdfDocument the loaded document, not yet given to the PDFViewer
 * @param {HTMLStyleElement} style the style element this mode owns
 */
export const hideRemovedMarks = async (pdfDocument: DocumentLike, style: HTMLStyleElement): Promise<void> => {
    const { removedIds } = await cachedScan(pdfDocument);
    for (const id of removedIds) pdfDocument.annotationStorage.setValue(id, { noView: true });
    style.textContent = removedIdsCss(removedIds);
};

/**
 * @param {string[]} removedIds the marks to hide
 * @return {string} CSS hiding their annotation-layer elements and every popup
 */
export const removedIdsCss = (removedIds: string[]): string => [
    '.annotationLayer .popupAnnotation, .annotationLayer .popup',
    ...removedIds.map((id) => `.annotationLayer [data-annotation-id="${CSS.escape(id)}"]`),
].join(',\n') + ' { display: none !important; }';
